import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Loader2 } from "lucide-react";

import { AppDialog } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/features/auth";
import {
  useRolesPermissionsStore,
  useUserListDerived,
  useUserStore,
} from "../hooks";
import { Button } from "@/components/ui/button";
import PermissionViewsTable from "./PermissionsViewTable";
import { useOrganizationStore } from "@/features/organization";
import { TemporaryPasswordField } from "./TemporaryPasswordField";
import { resolveRoleLabel } from "../utils/user-role-options";
import ViewOrgs from "@/components/ui/ViewOrgs";

const ADMIN_ROLES = ["system_admin", "business_unit"];

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatStatusLabel = (role = "") =>
  role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeRoleId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return String(value._id || value.id || value.roleId || value.$oid || "");
  }
  return String(value);
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const UserViewDialog = observer(({ user, onClose }) => {
  const { t } = useTran();
  const { userRole } = useAuthStore();
  const {
    loadManagedUser,
    getEditForm,
    getEditMeta,
    isLoading,
    getError,
    getUser,
    invalidateUser,
  } = useUserStore();
  const { roles, loadingRoles, fetchRoleList } = useRolesPermissionsStore();
  const { rolesWithPermissions, rolePermissionsToAllow, currentUserId } =
    useUserListDerived();
  const [reloadKey, setReloadKey] = useState(0);

  const viewDialogOpen = Boolean(user);
  const viewDialogUser = user;

  useEffect(() => {
    if (!viewDialogOpen || !viewDialogUser?.id) return;

    let isActive = true;
    const fetchDetails = async () => {
      try {
        await loadManagedUser(viewDialogUser.id, { force: reloadKey > 0 });
      } catch (_error) {
        if (!isActive) return;
      }
    };

    fetchDetails();

    return () => {
      isActive = false;
    };
  }, [viewDialogOpen, viewDialogUser?.id, reloadKey, loadManagedUser]);

  useEffect(() => {
    if (!viewDialogOpen) return;
    if (roles.length > 0 || loadingRoles) return;
    fetchRoleList();
  }, [viewDialogOpen, roles.length, loadingRoles, fetchRoleList]);

  const { tree: organizationTree } = useOrganizationStore();

  const flattenOrganizations = (orgs = []) => {
    let result = [];

    orgs.forEach((org) => {
      result.push({
        id: org.id,
        name: org.name,
      });

      if (org.children?.length) {
        result = result.concat(flattenOrganizations(org.children));
      }
    });

    return result;
  };

  const getSelectedOrganizations = (
    allOrganizations,
    selectedIds = [],
    user,
  ) => {
    const flatOrgs = flattenOrganizations(allOrganizations);

    let finalSelectedIds = [...selectedIds];

    if (user?.assignedToParentCompany) {
      const rootOrg = flatOrgs.find((org) => !org.parentId);

      if (rootOrg) {
        finalSelectedIds.push(String(rootOrg.id));
      }
    }

    const selectedIdSet = new Set(finalSelectedIds.map(String));

    return flatOrgs.filter((org) => selectedIdSet.has(String(org.id)));
  };

  const editForm = getEditForm(viewDialogUser?.id);
  const meta = getEditMeta(viewDialogUser?.id);
  const loading = Boolean(viewDialogUser?.id && isLoading(viewDialogUser.id));
  const loadError = viewDialogUser?.id ? getError(viewDialogUser.id) : "";
  const storedUser = viewDialogUser?.id ? getUser(viewDialogUser.id) : null;
  const resolvedUser = storedUser
    ? { ...(viewDialogUser || {}), ...storedUser }
    : viewDialogUser || {};

  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);
  const isCurrentUser = user?.role === userRole && user?.id === currentUserId;
  const isSuperAdmin = user?.role === "super_admin";
  const shouldShowCompany = isCompanyAdmin || isCurrentUser || isSuperAdmin;

  const displayName =
    editForm.name ||
    resolvedUser.displayName ||
    resolvedUser.name ||
    t("Not available");
  const username =
    editForm.username || resolvedUser.username || t("Not available");
  const email = editForm.email || resolvedUser.email || t("Not available");

  const selectedOrganizations = getSelectedOrganizations(
    organizationTree,
    editForm.organizationUnitId,
    resolvedUser,
  );

  const organizationOrgs =
    selectedOrganizations.length > 0
      ? selectedOrganizations.map((org) => ({
          id: org.id,
          name: org.name,
        }))
      : [
          {
            id:
              editForm.organizationUnitId ||
              resolvedUser.organizationUnitId ||
              null,
            name:
              editForm.organizationUnitName ||
              resolvedUser.organizationUnitName ||
              t("Not assigned"),
          },
        ];

  //  Orginal Code
  // const organizationLabel =
  //   editForm.organizationUnitName ||
  //   resolvedUser.organizationUnitName ||
  //   editForm.organizationUnitId ||
  //   resolvedUser.organizationUnitId ||
  //   t("Not assigned");

  const companyLabel =
    meta.company || resolvedUser.company || t("Not assigned");
  const roleLabel = resolvedUser.role
    ? resolveRoleLabel(
        resolvedUser.role,
        t,
        resolvedUser.role,
        resolvedUser.customRoleName,
      )
    : t("Not available");
  const isNewUser = resolvedUser?.isNew === true;
  const statusLabel = isNewUser
    ? t("Awaiting Login")
    : resolvedUser.status
      ? formatStatusLabel(resolvedUser.status)
      : t("Not available");
  const lockUntilLabel = resolvedUser?.lockUntil
    ? formatDate(resolvedUser.lockUntil)
    : null;

  const randomPass = resolvedUser?.randomPass;
  const permissionOverrides = resolvedUser?.permissionOverrides || {};
  const overrideMode =
    permissionOverrides.mode === "override" ? "override" : "inherit";
  const overrideAllow = Array.isArray(permissionOverrides.allow)
    ? permissionOverrides.allow
    : [];
  const overrideDeny = Array.isArray(permissionOverrides.deny)
    ? permissionOverrides.deny
    : [];
  const effectivePermissions = Array.isArray(resolvedUser?.effectivePermissions)
    ? resolvedUser.effectivePermissions
    : [];
  const inheritRoleId = permissionOverrides?.roleId;
  const inheritPermissions = useMemo(() => {
    if (overrideMode !== "inherit") return [];
    const targetRoleId = normalizeRoleId(inheritRoleId);
    let selectedRole = null;

    if (targetRoleId) {
      selectedRole = rolesWithPermissions.find((role) => {
        const roleId = normalizeRoleId(
          role?._id ?? role?.id ?? role?.key ?? role?.name,
        );
        return roleId && roleId === targetRoleId;
      });
    }

    if (!selectedRole) {
      const selectedRoleKey = normalizeRoleKey(
        resolvedUser?.customRoleName || resolvedUser?.role,
      );
      if (selectedRoleKey) {
        selectedRole = rolesWithPermissions.find((role) => {
          const roleNameKey = normalizeRoleKey(role?.name || role?.key);
          return roleNameKey === selectedRoleKey;
        });
      }
    }

    if (!selectedRole) return [];
    return rolePermissionsToAllow(selectedRole.permissions || []);
  }, [
    overrideMode,
    inheritRoleId,
    resolvedUser?.role,
    resolvedUser?.customRoleName,
    rolesWithPermissions,
    rolePermissionsToAllow,
  ]);
  const hasOverrideEntries =
    overrideAllow.length > 0 || overrideDeny.length > 0;
  const inheritVisiblePermissions =
    inheritPermissions.length > 0 ? inheritPermissions : effectivePermissions;

  const showEmptyState = !viewDialogUser?.id;
  const handleRetry = () => {
    if (!viewDialogUser?.id) return;
    invalidateUser(viewDialogUser.id);
    setReloadKey((prev) => prev + 1);
  };

  return (
    <AppDialog
      open={viewDialogOpen}
      onOpenChange={(nextOpen) => !nextOpen && onClose?.()}
      title={t("User Information")}
      size="lg"
    >
      {showEmptyState ? (
        <p className="text-sm text-muted-foreground">
          {t("Select a user to view details.")}
        </p>
      ) : loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("Loading user details...")}
        </div>
      ) : loadError ? (
        <div className="space-y-3 rounded-radius border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{loadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRetry}
          >
            {t("Try again")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-radius border border-border bg-muted/20 p-4">
            <p className="break-words text-lg font-medium text-foreground">
              {displayName}
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("Username")}
                </p>
                <p className="text-sm text-foreground font-medium">
                  {username}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("Email")}
                </p>
                <p className="text-sm text-foreground font-medium">{email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("Role")}
                </p>
                <p className="text-sm text-foreground font-medium">
                  {roleLabel}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {shouldShowCompany ? t("Company") : t("Organization")}
                </p>
                <p className="text-sm text-foreground font-medium">
                  {shouldShowCompany ? (
                    companyLabel
                  ) : (
                    <ViewOrgs orgs={organizationOrgs} tree={organizationTree} />
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-radius border border-border bg-background/60 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("Status")}
              </p>
              <Badge
                variant="outline"
                className={`
                        ${
                          resolvedUser.status === "active"
                            ? "border-green-500 text-green-500"
                            : resolvedUser.status === "locked"
                              ? "border-red-500 text-red-500"
                              : "border-yellow-500 text-yellow-500"
                        }
                      `}
              >
                {statusLabel}
              </Badge>
              {resolvedUser.status === "locked" && lockUntilLabel ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("Locked until {{time}}", { time: lockUntilLabel })}
                </p>
              ) : null}
            </div>

            <div className="rounded-radius border border-border bg-background/60 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("Created at")}
              </p>
              <p className="text-sm text-foreground font-medium">
                {meta.createdAt
                  ? formatDate(meta.createdAt)
                  : t("Not available")}
              </p>
            </div>
          </div>
          {randomPass ? <TemporaryPasswordField randomPass={randomPass} /> : ""}

          <div className="space-y-2 rounded-radius border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs uppercase text-muted-foreground">
                {overrideMode === "override"
                  ? t("Override Permissions")
                  : t("Inherit Permissions")}
              </h3>
              <span className="text-xs font-medium text-foreground">
                {t("Mode")}:{" "}
                {overrideMode === "override" ? t("Override") : t("Inherit")}
              </span>
            </div>
            {overrideMode === "override" ? (
              hasOverrideEntries ? (
                <PermissionViewsTable
                  userId={viewDialogUser?.id || null}
                  permissions={overrideAllow}
                  user={viewDialogUser}
                />
              ) : null
            ) : inheritVisiblePermissions.length > 0 ? (
              <PermissionViewsTable
                userId={viewDialogUser?.id || null}
                permissions={inheritVisiblePermissions}
                user={viewDialogUser}
              />
            ) : null}
          </div>

          {/* Effective Permission Section */}
          {/* {overrideMode !== "override" && (
            <div className="space-y-2 rounded-radius border border-border bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("Effective Permissions")}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {t("Total")}: {effectivePermissions.length}
                </span>
              </div>
              {effectivePermissions.length > 0 && (
                <PermissionViewsTable permissions={effectivePermissions} />
              )}
            </div>
          )} */}
        </div>
      )}
    </AppDialog>
  );
});

export default UserViewDialog;
